import type { Article } from "@/types/content";

export const articles: Article[] = [
  {
    id: "bookstore-second-life",
    title: "当城市开始写诗：独立书店的第二次青春",
    subtitle: "在算法推荐之外，一间三十平米的店如何重新组织人与书的关系",
    category: "culture",
    tags: ["独立书店", "城市空间", "阅读"],
    author: {
      name: "林知远",
      role: "文化主笔",
      bio: "十年出版行业观察者，长期记录城市文化空间的兴衰与变形。",
    },
    date: "2026-10-14",
    readMinutes: 9,
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20independent%20bookstore%20interior%20with%20tall%20wooden%20shelves%2C%20stacks%20of%20books%20on%20a%20long%20table%2C%20afternoon%20sunlight%20through%20window%2C%20warm%20cream%20and%20terracotta%20tones%2C%20editorial%20photography%2C%20soft%20natural%20light%2C%20cinematic%20composition&width=1200&height=800&seq=dy-bookstore-cover&orientation=landscape",
    summary:
      "过去三年，全国新开的独立书店数量不降反升。它们不再靠卖书赚钱，而是把空间做成一种慢速的社交基础设施。",
    views: 18642,
    featured: true,
    sections: [
      {
        id: "sec-1",
        heading: "一、把书架降低三十厘米",
        paragraphs: [
          "老周的书店开在一条卖五金和窗帘的街上。第一次去的人常常走过头，因为门口没有招牌，只有一块手写的木牌，上面写着当天到货的三本书名。",
          "他说自己做过最正确的决定，是把靠墙的书架整体降低了三十厘米。「站着挑书的人，脖子会累；坐着翻书的人，才会留下来。」这个细节听起来像玄学，但店里的座位确实从四个变成了十一个，周末的下午几乎坐满。",
          "在电商把图书价格压到接近成本线之后，实体书店卖的不再是「书」，而是「遇到一本书的过程」。这个过程需要身体参与：抽出一本，翻两页，放回去，再抽出旁边那本。算法永远无法复制这种偶然。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Close%20view%20of%20a%20low%20wooden%20bookshelf%20in%20a%20cozy%20bookstore%2C%20handwritten%20paper%20labels%2C%20warm%20lamp%20light%2C%20muted%20cream%20and%20rust%20color%20palette%2C%20shallow%20depth%20of%20field%2C%20editorial%20interior%20photography&width=1200&height=750&seq=dy-bookstore-shelf&orientation=landscape",
        imageCaption: "降低后的书架让读者可以坐着挑书，也让空间的重心从墙面移回人身上。",
      },
      {
        id: "sec-2",
        heading: "二、书店卖的是时间，不是纸",
        paragraphs: [
          "我们统计了十二家存活超过五年的独立书店的收入结构：图书销售平均只占 38%，其余来自咖啡、活动、文创和空间租借。这个数字常常被解读为「书店不务正业」，但更准确的说法是，书店把「停留时间」变成了可以定价的产品。",
          "一场新书分享会能带来四十到六十人，人均消费三十五元，但真正的收益发生在活动结束之后：有三分之一的人会成为月度常客。书店的复购逻辑不是折扣，而是「这里发生过一件我记得的事」。",
          "这也解释了为什么越来越多书店开始做深夜场。晚上九点之后的两个小时，是城市里最安静、也最容易被记住的时间段。",
        ],
      },
      {
        id: "sec-3",
        heading: "三、第二次青春，也可能是最后一次",
        paragraphs: [
          "乐观的数据背后有代价。我们接触的店主中，超过一半的人同时在做第二份工作；有三家书店的租约将在明年到期，续约成本预计上涨 40%。",
          "文化空间的价值很难被租金模型承认，这是全世界城市共同面对的问题。真正有效的解法往往不在书店自己身上，而在街区：当一条街上的咖啡馆、修理铺、小剧场都活下来，书店才有机会活下来。",
          "老周对此倒是平静。他说自己只关心一件事：明天下午有没有人推门进来，在窗边坐两个小时，然后带走一本他本来没打算买的书。「只要有这个，就还能开下去。」",
        ],
      },
    ],
  },
  {
    id: "jianzhan-tea-bowl",
    title: "手艺人笔记：在一只建盏里看见时间的釉色",
    subtitle: "窑变不可控，但手上的分寸可以练习一万次",
    category: "culture",
    tags: ["手作", "建盏", "非遗"],
    author: {
      name: "沈禾",
      role: "特约作者",
      bio: "长期走访手工艺现场，关注技艺传承中的身体经验与材料语言。",
    },
    date: "2026-10-11",
    readMinutes: 8,
    cover:
      "https://readdy.ai/api/search-image?query=Dark%20glazed%20Jian%20tea%20bowl%20with%20subtle%20iridescent%20iron%20spots%2C%20resting%20on%20a%20raw%20clay%20workbench%2C%20warm%20side%20light%2C%20earthy%20cream%20and%20deep%20rust%20tones%2C%20macro%20editorial%20craft%20photography%2C%20soft%20shadows&width=1200&height=800&seq=dy-jianzhan-cover&orientation=landscape",
    summary:
      "一只建盏的成败，在入窑之前就已经决定了七成。剩下三成，靠的是火与土的偶然。",
    views: 12036,
    featured: true,
    sections: [
      {
        id: "sec-1",
        heading: "一、泥料先说话",
        paragraphs: [
          "在福建建阳的一间工作室里，陈师傅每天开始工作的第一件事不是拉坯，而是用手掌压一压前一天醒好的泥。「泥太软，拉起来的壁厚不匀；太硬，收口会裂。」他说这话时没有抬头，手掌一直在泥上小幅移动。",
          "建盏的胎体含铁量高，烧成收缩大，稍微厚一点就会在高温里塌下来。一个看起来朴素的小碗，背后是十几次失败后的手感记忆。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、釉色的偶然与必然",
        paragraphs: [
          "所谓「窑变」，指釉面在 1300 度高温中因气氛变化产生的不可控效果。兔毫、油滴、鹧鸪斑，这些名字来自视觉联想，但在工艺上对应的是完全不同的温度曲线与釉层厚度。",
          "陈师傅的窑一年只开二十次。每次装窑之前，他会留三个位置给自己「赌一把」——同样的釉、同样的温度，出来的效果仍然可能完全不同。这也是建盏最迷人的部分：它承认人力有边界。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Traditional%20wood%20fired%20kiln%20interior%20with%20rows%20of%20dark%20ceramic%20bowls%20glowing%20in%20warm%20orange%20firelight%2C%20artisan%20workshop%20atmosphere%2C%20smoky%20air%2C%20documentary%20craft%20photography%2C%20rich%20earthy%20tones&width=1200&height=750&seq=dy-jianzhan-kiln&orientation=landscape",
        imageCaption: "开窑的瞬间，温度仍然是主角。",
      },
      {
        id: "sec-3",
        heading: "三、被使用，才算完成",
        paragraphs: [
          "很多非遗项目最大的困境是「只能看，不能用」。陈师傅坚持每只盏都要经过至少一个月的日常使用测试，泡茶、清洗、晾干。「釉面会随着使用变得温润，这叫养盏。养出来的东西，才是活的。」",
          "他的儿子去年回到工作室，负责把制作过程拍成短视频。视频里没有煽情配乐，只有拉坯的转盘声和窑火的噼啪声。「有人看完说想学，我就告诉他，先来揉三个月泥。」",
        ],
      },
    ],
  },
  {
    id: "short-drama-actors",
    title: "从配角到主角：短剧演员的黄金三年",
    subtitle: "一天拍二十场戏，行业在高速奔跑，人也在被重新定价",
    category: "entertainment",
    tags: ["短剧", "演员", "行业观察"],
    author: {
      name: "周映",
      role: "娱乐记者",
      bio: "跟踪影视工业化与新兴内容形态，采访过两百余位剧组从业者。",
    },
    date: "2026-10-15",
    readMinutes: 10,
    cover:
      "https://readdy.ai/api/search-image?query=Film%20set%20with%20softbox%20lights%20and%20camera%20crew%20silhouettes%2C%20warm%20amber%20lighting%20in%20a%20small%20studio%2C%20dark%20curtains%2C%20cinematic%20behind%20the%20scenes%20editorial%20photography%2C%20rich%20terracotta%20accents&width=1200&height=800&seq=dy-shortdrama-cover&orientation=landscape",
    summary:
      "竖屏短剧用两年时间跑完了长剧十年的工业化路径。演员的机会变多了，但「被看见」的周期也被压缩到以周计算。",
    views: 24118,
    featured: true,
    sections: [
      {
        id: "sec-1",
        heading: "一、三天一部，一天二十场",
        paragraphs: [
          "上午九点，横店某栋改造过的写字楼里，第四组正在拍一场雨夜争吵戏。没有真的雨，只有两盏灯和一根水管。导演喊「过」的时候，演员的头发还在滴水，下一场戏的服装已经递到手上。",
          "一部八十集的竖屏短剧，常规拍摄周期是七到十天。演员平均每天要完成十五到二十场戏，台词量接近一集长剧。这种强度下，「准确」比「细腻」更重要——情绪需要在三秒内建立，五秒内转折。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、被重新定价的表演",
        paragraphs: [
          "短剧的镜头语言决定了表演方式：竖屏、近景、高密度特写。夸张一点的表情在小屏幕上刚刚好，收敛的演法反而显得「没戏」。这让一批舞台剧和长剧出身的演员一开始很不适应。",
          "但市场很快给出了反馈。我们统计了今年上半年热度前五十的短剧主演，其中 42% 没有长剧主演经历，却拥有稳定的粉丝群和商业代言。「出身」这件事，在这个赛道里被大幅稀释了。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Director%20monitor%20on%20a%20film%20set%20showing%20a%20vertical%20video%20frame%2C%20hands%20holding%20a%20script%2C%20warm%20studio%20light%2C%20shallow%20depth%20of%20field%2C%20documentary%20style%20production%20photography&width=1200&height=750&seq=dy-shortdrama-monitor&orientation=landscape",
        imageCaption: "竖屏画幅改变了整个行业的表演语法。",
      },
      {
        id: "sec-3",
        heading: "三、黄金三年之后",
        paragraphs: [
          "高速生产也意味着快速消耗。一位从业六年的选角导演告诉我，短剧演员的职业半衰期大约是三年：「观众记脸很快，忘得也很快。」",
          "于是分化开始出现：一部分人转向长剧和电影，用短剧积累的知名度换取更慢的节奏；另一部分人转向幕后，做制片、做剧本，把对节奏的理解变成生产力。",
          "「这个行业现在最缺的不是演员，是能把三分钟讲清楚的人。」她说。",
        ],
      },
    ],
  },
  {
    id: "documentary-return",
    title: "纪录片复兴：年轻人为什么重新坐回屏幕前",
    subtitle: "当信息过载到极限，长镜头反而成了一种休息",
    category: "entertainment",
    tags: ["纪录片", "观影", "内容消费"],
    author: {
      name: "周映",
      role: "娱乐记者",
      bio: "跟踪影视工业化与新兴内容形态，采访过两百余位剧组从业者。",
    },
    date: "2026-10-09",
    readMinutes: 7,
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20independent%20cinema%20with%20red%20velvet%20seats%20and%20a%20projector%20beam%20cutting%20through%20dusty%20air%2C%20warm%20dim%20lighting%2C%20cream%20and%20deep%20amber%20tones%2C%20atmospheric%20editorial%20photography%2C%20cinematic%20composition&width=1200&height=800&seq=dy-documentary-cover&orientation=landscape",
    summary:
      "过去一年，纪录片在流媒体的完播率首次超过剧集。慢下来的观看，正在变成一种稀缺体验。",
    views: 9842,
    sections: [
      {
        id: "sec-1",
        heading: "一、倍速时代的逆行者",
        paragraphs: [
          "平台数据显示，纪录片用户的平均单次观看时长是 47 分钟，远高于剧集的 22 分钟。更有意思的是，纪录片观众使用倍速的比例最低，只有 11%。",
          "「看纪录片的时候，我不想再被推着走。」一位 26 岁的观众这样说。她的手机里有六个内容 App，但每周固定留两个晚上给纪录片，「那两个小时里，没有人问我下一个想看什么」。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、从知识补充到情绪出口",
        paragraphs: [
          "早期的国产纪录片承担的是知识普及功能，观众带着「学习」的目的进入。这一轮复兴的逻辑不同：自然、手艺、饮食类纪录片提供的是一种低强度的陪伴感。",
          "制作方也在调整。近两年上线的项目里，旁白比例明显下降，环境声和长镜头增加。「我们不想解释太多，让画面自己待一会儿。」一位制片人说。",
        ],
      },
      {
        id: "sec-3",
        heading: "三、院线的窄门",
        paragraphs: [
          "与流媒体的热度相比，纪录片进院线依然艰难。今年票房过千万的国产纪录片只有四部，大多数项目的回收依赖平台采购与海外发行。",
          "但变化正在发生。一些纪录片开始采用「分线发行 + 影迷社群」的方式，先在十个城市做点映，靠口碑扩散。单厅票房不高，但上座率稳定。",
          "「我们不需要所有人来看，」导演说，「只需要想看的人知道它在哪。」",
        ],
      },
    ],
  },
  {
    id: "slow-kitchen-weekend",
    title: "把周末还给厨房：都市人的慢食实验",
    subtitle: "三小时做一顿饭，不是为了吃，是为了把时间重新拿回来",
    category: "life",
    tags: ["厨房", "慢生活", "周末"],
    author: {
      name: "苏晚",
      role: "生活方式编辑",
      bio: "写城市生活实验与消费观察，相信小的改变比大的决心更持久。",
    },
    date: "2026-10-13",
    readMinutes: 6,
    cover:
      "https://readdy.ai/api/search-image?query=Hands%20slicing%20fresh%20vegetables%20on%20a%20wooden%20cutting%20board%20in%20a%20bright%20home%20kitchen%2C%20scattered%20herbs%20and%20ceramic%20bowls%2C%20morning%20sunlight%2C%20warm%20cream%20and%20olive%20tones%2C%20editorial%20food%20photography%2C%20soft%20natural%20light&width=1200&height=800&seq=dy-kitchen-cover&orientation=landscape",
    summary:
      "我们邀请了十二位上班族，记录他们连续四周的周末厨房。结果显示：做饭时长与周末满意度呈明显正相关。",
    views: 15320,
    featured: true,
    sections: [
      {
        id: "sec-1",
        heading: "一、从「效率料理」到「无目的烹饪」",
        paragraphs: [
          "工作日的厨房是效率机器：十五分钟，一口锅，两个菜。周末的厨房可以是另一回事——炖一锅需要两小时的汤，或者只是把三种豆子挑干净。",
          "「慢」的关键不是时长，而是没有明确产出目标。你不需要在限定时间内端出什么，过程本身就是目的。这一点，恰好是大部分都市生活里最缺的东西。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、四周实验的三个发现",
        paragraphs: [
          "第一，准备阶段比烹饪阶段更治愈。洗菜、切菜的动作有稳定的节奏，不需要决策，大脑处于一种接近放空的状态。",
          "第二，一起做饭比一起吃饭更能建立关系。实验中三对伴侣表示，共同处理食材时发生的对话，比餐桌上的对话更真实。",
          "第三，剩菜不是失败。愿意接受「这顿没做完」的人，第二周继续下厨的概率高出 60%。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Rustic%20dining%20table%20with%20a%20slow%20cooked%20meal%20in%20ceramic%20dishes%2C%20linen%20napkins%2C%20fresh%20bread%2C%20warm%20afternoon%20light%20from%20a%20window%2C%20muted%20cream%20and%20terracotta%20palette%2C%20editorial%20lifestyle%20photography&width=1200&height=750&seq=dy-kitchen-table&orientation=landscape",
        imageCaption: "一顿饭的价值，往往不在味道本身。",
      },
      {
        id: "sec-3",
        heading: "三、一个可执行的最小方案",
        paragraphs: [
          "如果你也想试试，可以从这个方案开始：周六上午去一次菜市场，只买当季的三样蔬菜和一样主食；回家后花四十分钟处理食材，不设时间限制；剩下的交给锅。",
          "不要拍照，不要发社交平台。让这件事只属于你自己——至少第一次是这样。",
        ],
      },
    ],
  },
  {
    id: "minimalism-after",
    title: "断舍离之后：极简主义如何重塑消费习惯",
    subtitle: "扔东西只是开始，真正的难题是下一次购买",
    category: "life",
    tags: ["极简", "消费", "整理"],
    author: {
      name: "苏晚",
      role: "生活方式编辑",
      bio: "写城市生活实验与消费观察，相信小的改变比大的决心更持久。",
    },
    date: "2026-10-06",
    readMinutes: 7,
    cover:
      "https://readdy.ai/api/search-image?query=Serene%20minimal%20room%20with%20a%20single%20wooden%20chair%2C%20a%20small%20ceramic%20vase%20and%20soft%20morning%20light%20on%20a%20cream%20wall%2C%20calm%20warm%20neutral%20palette%2C%20editorial%20interior%20photography%2C%20gentle%20shadows&width=1200&height=800&seq=dy-minimal-cover&orientation=landscape",
    summary:
      "整理热潮过去五年，那些真正坚持下来的人，改变了不是收纳方式，而是对「需要」的判断标准。",
    views: 11025,
    sections: [
      {
        id: "sec-1",
        heading: "一、扔完之后，房间又满了",
        paragraphs: [
          "很多人有过这样的循环：周末大扫除，扔掉三袋东西，两个月后柜子重新塞满。问题不在整理技术，而在入口——如果不改变购买行为，整理只是一次延迟。",
          "「极简不是拥有得少，是每一件东西都有明确的用途。」一位受访者说。她的做法是给每个抽屉设定容量上限，满了就必须替换，而不是增加。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、延迟购买的四十八小时",
        paragraphs: [
          "实验中，我们要求参与者在购买非必需品前等待四十八小时，并写下三条购买理由。结果有 63% 的商品在两天后被放弃。",
          "放弃的理由高度集中：「已经有类似的了」「只是当时打折」「想象的使用场景不成立」。这三条几乎覆盖了所有冲动消费。",
          "更有意思的是，真正被买下的那 37%，使用频率明显高于对照组。延迟没有降低消费，而是提高了消费的质量。",
        ],
      },
      {
        id: "sec-3",
        heading: "三、少一点，但好一点",
        paragraphs: [
          "极简主义在中国城市的落地版本，和日式「空无一物」并不相同。我们更在意的是「耐用」与「可修」。",
          "受访者中，有 41% 的人在过去一年修过至少一件物品——补鞋、换拉链、修台灯。这个数字比五年前高出近一倍。",
          "这或许是整理热潮留下的真正遗产：不是更空的房间，而是更长的使用周期。",
        ],
      },
    ],
  },
  {
    id: "editor-in-content-flood",
    title: "内容过载时代，我们还需要编辑吗",
    subtitle: "当生成变得廉价，判断力成为最贵的东西",
    category: "opinion",
    tags: ["内容行业", "编辑", "媒介"],
    author: {
      name: "陈默",
      role: "主编",
      bio: "二十年媒体从业者，关注内容生产机制与平台生态的变化。",
    },
    date: "2026-10-12",
    readMinutes: 11,
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20desk%20with%20printed%20manuscripts%20covered%20in%20red%20pen%20marks%2C%20a%20cup%20of%20coffee%2C%20reading%20glasses%2C%20warm%20lamp%20light%2C%20cream%20paper%20and%20terracotta%20accents%2C%20top%20down%20editorial%20photography&width=1200&height=800&seq=dy-editor-cover&orientation=landscape",
    summary:
      "供给过剩不会自动带来质量提升。相反，它让「选择」这件事变得更昂贵，也让编辑的价值重新显影。",
    views: 20874,
    featured: true,
    sections: [
      {
        id: "sec-1",
        heading: "一、生产的门槛消失了",
        paragraphs: [
          "二十年前，写一篇能公开发表的文章需要经过选题会、采访、编辑、校对、排版。今天，这些环节都可以被压缩成一次点击。",
          "门槛消失的直接后果是供给爆炸，间接后果是注意力稀缺。当所有人都在生产，稀缺的就变成了「被认真读完」。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、编辑做的其实是否定工作",
        paragraphs: [
          "外界常以为编辑的工作是修改错别字，实际上大部分时间花在否定上：这个选题不值得做，这个论据不成立，这段材料应该删掉。",
          "否定需要成本。它要求编辑比作者更了解读者，也要求他有勇气承担判断错误的后果。这两点在算法面前都不占优势——算法只需要点击率。",
          "但长期来看，只有否定的积累才能形成风格。一个媒体之所以是它自己，不是因为发了什么，而是因为没发什么。",
        ],
        image:
          "https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20hand%20writing%20corrections%20with%20a%20red%20pen%20on%20a%20printed%20article%2C%20stack%20of%20papers%20and%20a%20notebook%20nearby%2C%20warm%20desk%20lamp%20light%2C%20muted%20cream%20background%2C%20editorial%20detail%20photography&width=1200&height=750&seq=dy-editor-notes&orientation=landscape",
        imageCaption: "红笔划掉的部分，往往比留下的更能说明立场。",
      },
      {
        id: "sec-3",
        heading: "三、给读者的三条建议",
        paragraphs: [
          "第一，关注「谁在负责」。一篇内容如果找不到署名的人，它的可信度天然要打折。",
          "第二，区分「信息」与「判断」。信息可以搬运，判断不能。你真正需要的是后者。",
          "第三，允许自己少看一点。信息摄入不是越多越好，就像吃饭一样，过量会带来负担。",
        ],
      },
    ],
  },
  {
    id: "vinyl-comeback",
    title: "旧唱片新听法：黑胶回潮背后的听觉乡愁",
    subtitle: "在流媒体时代，为什么有人愿意为一面 22 分钟付费",
    category: "culture",
    tags: ["黑胶", "音乐", "消费文化"],
    author: {
      name: "林知远",
      role: "文化主笔",
      bio: "十年出版行业观察者，长期记录城市文化空间的兴衰与变形。",
    },
    date: "2026-10-03",
    readMinutes: 8,
    cover:
      "https://readdy.ai/api/search-image?query=Vintage%20turntable%20with%20a%20spinning%20vinyl%20record%2C%20warm%20wooden%20shelf%20of%20record%20sleeves%20in%20the%20background%2C%20soft%20golden%20lamp%20light%2C%20cream%20and%20amber%20tones%2C%20editorial%20still%20life%20photography%2C%20shallow%20depth%20of%20field&width=1200&height=800&seq=dy-vinyl-cover&orientation=landscape",
    summary:
      "中国黑胶唱片销量连续六年增长。买它的人未必有唱机，但他们都想要一种「完整听完」的经验。",
    views: 8731,
    sections: [
      {
        id: "sec-1",
        heading: "一、一面二十二分钟的限制",
        paragraphs: [
          "黑胶的物理限制决定了专辑的结构：一面最多二十二分钟，中间必须翻面。这个限制在过去是技术约束，在今天变成了一种创作语法。",
          "「你会被迫考虑顺序。」一位独立音乐人说。流媒体时代的听众随机播放，而黑胶要求创作者安排情绪的前后关系。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、买唱片的人，和听唱片的人",
        paragraphs: [
          "数据显示，国内黑胶购买者中，拥有唱机的比例不足一半。剩下的购买动机包括：收藏、装饰、支持喜欢的音乐人。",
          "这让黑胶带上了某种「周边」属性，但也带来了意想不到的结果——唱片店重新成为社交场所。周末的试听区常常需要排队。",
        ],
      },
      {
        id: "sec-3",
        heading: "三、声音的仪式感",
        paragraphs: [
          "把唱片从封套里取出、放上转盘、落下唱针，这个过程需要三十秒。三十秒的等待，会让接下来的声音变得郑重。",
          "在一个可以随时跳过任何内容的时代，愿意等待本身就是一种表达。",
        ],
      },
    ],
  },
  {
    id: "night-running-map",
    title: "城市夜跑地图：在路灯下重新认识街区",
    subtitle: "五条路线，五个完全不同的城市表情",
    category: "life",
    tags: ["跑步", "城市", "运动"],
    author: {
      name: "苏晚",
      role: "生活方式编辑",
      bio: "写城市生活实验与消费观察，相信小的改变比大的决心更持久。",
    },
    date: "2026-09-28",
    readMinutes: 6,
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20city%20street%20at%20night%20with%20wet%20asphalt%20reflecting%20warm%20streetlights%2C%20a%20lone%20runner%20silhouette%20in%20the%20distance%2C%20cinematic%20warm%20amber%20and%20deep%20charcoal%20tones%2C%20atmospheric%20urban%20photography&width=1200&height=800&seq=dy-nightrun-cover&orientation=landscape",
    summary:
      "晚上九点之后的街道属于另一批人。跑步是最快进入这座城市夜间版本的方式。",
    views: 7304,
    sections: [
      {
        id: "sec-1",
        heading: "一、为什么是晚上",
        paragraphs: [
          "白天的城市为效率服务：通勤路线、外卖动线、写字楼电梯。晚上九点之后，这些功能褪去，街道露出它本来的样子——宽度、坡度、树影。",
          "夜跑者往往比白天更熟悉自己的社区。因为没有目的地，路线可以随时改变。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、五条路线的共同点",
        paragraphs: [
          "我们整理的五条路线，长度都在 4 到 6 公里之间，共同点是：避开主干道、经过一段水边、终点附近有便利店。",
          "最后一条最实用。跑完能买到一瓶水，这件事决定了你明天还愿不愿意出门。",
        ],
      },
      {
        id: "sec-3",
        heading: "三、安全比配速重要",
        paragraphs: [
          "夜跑的三个基本建议：穿有反光条的衣服，不要戴降噪耳机，告诉家人你的路线。",
          "配速、心率、里程都可以慢慢来。先把出门这件事变成习惯，其他数字才有意义。",
        ],
      },
    ],
  },
  {
    id: "music-festival-economy",
    title: "音乐节现场经济学：一场演出如何带动一座城",
    subtitle: "门票只占收入的三成，剩下七成发生在场地之外",
    category: "entertainment",
    tags: ["音乐节", "演出经济", "城市"],
    author: {
      name: "周映",
      role: "娱乐记者",
      bio: "跟踪影视工业化与新兴内容形态，采访过两百余位剧组从业者。",
    },
    date: "2026-09-25",
    readMinutes: 9,
    cover:
      "https://readdy.ai/api/search-image?query=Outdoor%20music%20festival%20stage%20at%20dusk%20with%20warm%20amber%20lights%20and%20a%20dense%20crowd%20silhouette%2C%20dust%20in%20the%20air%2C%20cinematic%20wide%20shot%2C%20muted%20cream%20and%20terracotta%20tones%2C%20editorial%20concert%20photography&width=1200&height=800&seq=dy-festival-cover&orientation=landscape",
    summary:
      "一场三万人规模的中型音乐节，能给举办地带来什么？我们用一份成本表回答了这个问题。",
    views: 13980,
    sections: [
      {
        id: "sec-1",
        heading: "一、成本表的第一行",
        paragraphs: [
          "舞台、音响、安保、保险、艺人费用，五项占了一场音乐节总成本的 70%。其中艺人费用弹性最大，从总成本的 25% 到 45% 不等。",
          "真正决定盈亏的往往不是票房，而是赞助。一个中型音乐节的赞助收入可以占到总收入的 40%，远超门票。",
        ],
      },
      {
        id: "sec-2",
        heading: "二、溢出效应怎么算",
        paragraphs: [
          "观众的平均停留时间是 1.6 天，这意味着住宿、餐饮、交通的消费会溢出到城市其他行业。",
          "但溢出并不均匀。场地三公里内的商户受益最明显，而城市远端的商家几乎无感。这也是为什么越来越多的音乐节选择与地方政府合作，把流量导向特定区域。",
        ],
      },
      {
        id: "sec-3",
        heading: "三、热闹之后的安静",
        paragraphs: [
          "音乐节的经济账好算，文化账难算。一个城市真正留下的，可能不是那三天的票房，而是此后愿意再来一次的年轻人。",
          "「我们最怕的是，办完之后大家只记得堵车。」一位主办方说。",
        ],
      },
    ],
  },
];